import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/** Public URL for a file stored in the private "media" bucket (served via proxy). */
export function mediaUrl(path: string): string {
  return `${SUPABASE_URL}/functions/v1/media-proxy/${path.replace(/^\/+/, "")}`;
}

/** True for URLs served by our resizing media-proxy. */
function isProxyUrl(url: string): boolean {
  return /\/functions\/v1\/media-proxy\//.test(url);
}

/**
 * Append width/quality params so the proxy resizes + compresses the image to
 * exactly the size we render (Shopify-style `?w=450`). No-op for non-proxy URLs.
 */
export function sizedImage(url: string, width: number, quality = 78): string {
  if (!url || !isProxyUrl(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}w=${Math.round(width)}&q=${quality}`;
}

/**
 * Build a responsive srcset (1x + 2x for retina) plus a default `src`.
 * Use with a matching `sizes` attribute on the <img>.
 */
export function imageSrcSet(url: string, width: number, quality = 78) {
  if (!url || !isProxyUrl(url)) {
    return { src: url, srcSet: undefined as string | undefined };
  }
  return {
    src: sizedImage(url, width, quality),
    srcSet: `${sizedImage(url, width, quality)} 1x, ${sizedImage(url, width * 2, quality)} 2x`,
  };
}

/** True for our own immutable asset-CDN URLs (/__l5e/...) — same-origin or absolute. */
function isAssetUrl(url: string): boolean {
  return url.includes("/__l5e/");
}

/**
 * Route an asset-CDN screenshot through the resizing media-proxy so we serve a
 * thumbnail-sized image instead of the full-resolution PNG. The CDN itself
 * can't resize, so this is where the big byte savings come from.
 *
 * - media-proxy URLs → resized via `sizedImage` (existing behaviour)
 * - /__l5e/ asset URLs → proxied with ?src= (JPEG by default; opaque screenshots)
 * - anything else → returned untouched
 */
export function assetImage(
  url: string,
  width: number,
  opts: { quality?: number; jpeg?: boolean } = {},
): { src: string; srcSet?: string } {
  const { quality = 78, jpeg = true } = opts;
  if (!url) return { src: url };
  if (isProxyUrl(url)) return imageSrcSet(url, width, quality);
  if (!isAssetUrl(url)) return { src: url };

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://devcore.systems";
  const abs = url.startsWith("http") ? url : `${origin}${url}`;
  // The proxy only accepts https asset hosts; on local http dev, serve original.
  if (!abs.startsWith("https://")) return { src: url };
  const build = (w: number) => {
    const p = new URLSearchParams({ src: abs, w: String(Math.round(w)), q: String(quality) });
    if (jpeg) p.set("fmt", "jpeg");
    return `${SUPABASE_URL}/functions/v1/media-proxy?${p.toString()}`;
  };
  return {
    src: build(width),
    srcSet: `${build(width)} 1x, ${build(width * 2)} 2x`,
  };
}


/**
 * Upload a file to the media bucket under `folder/` and return its public proxy URL.
 */
export async function uploadMedia(folder: string, file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
  const safe = file.name
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9-]/g, "-")
    .slice(0, 40);
  const path = `${folder}/${crypto.randomUUID()}-${safe}.${ext}`;
  const { error } = await supabase.storage.from("media").upload(path, file, {
    contentType: file.type || undefined,
    upsert: false,
  });
  if (error) throw error;
  return mediaUrl(path);
}
